// src/layout/MainLayout.tsx
import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, styled, Toolbar } from '@mui/material';
import AppHeader from './AppHeader';
import AppFooter from './AppFooter';
import AdPlaceholder from '../components/common/AdPlaceholder';
import { useAppSelector } from '../app/hooks'; // Import hook to access Redux state

// Define props interface for the styled component
interface MainContentProps {
    isSettingsPage: boolean;
}

// Styled component for the main content area
const MainContent = styled('main')<MainContentProps>(({ theme, isSettingsPage }) => ({
    flexGrow: 1,
    // Dynamically apply padding based on the route
    padding: isSettingsPage ? theme.spacing(1, 1) : theme.spacing(6, 3),
    display: 'flex',
    justifyContent: 'center',
    maxWidth: '100%',
    width: '1200px',
    [theme.breakpoints.down('lg')]: {
        width: '100%',
        padding: isSettingsPage ? theme.spacing(1, 1) : theme.spacing(6, 3),
    },
}));

const MainLayout: React.FC = () => {
    const location = useLocation();
    const { user } = useAppSelector(state => state.auth); // Get user from Redux state

    // Determine if ads should be enabled from environment variables.
    const adsEnabled = process.env.REACT_APP_ADS_ENABLED === 'true';

    // Logic to show ads ONLY on tool pages
    const toolPageRegex = /^\/tools\/.+\/.+/;
    const showAdsOnPage = toolPageRegex.test(location.pathname);

    // UPDATED: Check if the user is ad-free
    const isUserAdFree = user?.isAdFree && user.adFreeUntil && user.adFreeUntil > Date.now();

    // Final condition to show ads
    const shouldShowAds = adsEnabled && showAdsOnPage && !isUserAdFree;

    // Check if the current route is the settings page
    const isSettingsPage = location.pathname === '/settings';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <AppHeader />
            <Toolbar /> {/* Spacer for the fixed AppHeader */}

            <Box sx={{ display: 'flex', flexGrow: 1, width: '100%', justifyContent: 'center' }}>
                {shouldShowAds && <AdPlaceholder />}
                <MainContent isSettingsPage={isSettingsPage}>
                    <Outlet />
                </MainContent>
                {shouldShowAds && <AdPlaceholder />}
            </Box>

            <AppFooter />
        </Box>
    );
};

export default MainLayout;